import { TDomesticCoupon } from '~/api/services/coupon/index.type'
import { toKRW } from '~/api/utils/krNumberToString'
import { formatISODate } from '~/api/utils/datetime'
import { PropertyMashup } from '~/api/routes/domestic/order/controller/type'
import { MaxCouponDiscountPropertyRequiredParams } from '~/api/converters/domestic/order/Discount'
import { IDomesticOrderDiscountCouponSelectModalCoupon } from '~/api/converters/domestic/order/Discount/CouponSelectModal/index.type'
import { getIsPropertyB2BCoupon } from '~/api/converters/domestic/order/Coupons/MaxDiscountCoupons/getDomesticCouponMaxDiscount'

/**
 * PLCC (놀쿠폰) 여부 조회
 */
export const getIsNolCoupon = (couponType: string): boolean => {
  return couponType === 'PLCC'
}

/**
 * 국내 숙소 상품쿠폰 사용 불가 사유
 */
const domesticCouponUnavailableReasonsText = {
  unusable: '사용 불가능한 쿠폰입니다.',
  stock: '선착순 마감된 쿠폰입니다.',
  applicableminprice: '{applicableminprice} 이상 상품에 적용 가능합니다.',
  used: '이미 쿠폰을 사용하셨습니다.',
  usedbyothers: '이미 NOL 외 타 서비스에서 쿠폰을 발급 또는 사용하셨습니다.',
  place: '쿠폰 사용가능 업체 조건이 맞지 않습니다.',
  deniedroomtype: '숙박페스타 쿠폰 사용이 불가한 객실입니다.',
  roomprice: '객실가격 조건이 맞지 않습니다.',
  checkoutdateperiod: '퇴실일 : ~ {checkinPeriodEndAt}인 상품에만 사용 가능합니다.',
  millioncouponerror: '대행사와의 네트워크 이상으로 사용 불가합니다.',
  unknownerror: '대행사와의 네트워크 이상으로 사용 불가합니다.',
}

/**
 * 상품 쿠폰 9, 11 번 사용 불가능 사유 우선 순위: 낮을 수록 높음
 */
const domesticCouponUnavailableReasons = {
  unusable: 1,
  stock: 2,
  applicableminprice: 3,
}

/**
 * 상품 쿠폰의 사유를 우선적을 높이기 위해 10번 이후의 순위 설정.
 * 상품 쿠폰과 숙박 대전 쿠폰의 사유가 동시에 올 수 없다고 하지만 혹시 모를 상황 대비
 * 숙박대전 쿠폰 99번 사용 불가능 사유 우선 순위: 낮을 수록 높음
 */
const domesticMillionUnavailableReasons = {
  used: 11,
  usedbyothers: 12,
  place: 13,
  deniedroomtype: 14,
  roomprice: 15,
  checkoutdateperiod: 16,
  millioncouponerror: 17,
  unknownerror: 18,
}

/**
 * 국내 숙소 상품쿠폰 사용 불가 사유 우선 순위 정렬
 */
export const sortUnavailableReason = (unavailableReasons: string[]): string[] => {
  return unavailableReasons
    .map(reason => reason.toLowerCase())
    .sort((a, b) => {
      return (
        (domesticCouponUnavailableReasons[a] || domesticMillionUnavailableReasons[a]) -
        (domesticCouponUnavailableReasons[b] || domesticMillionUnavailableReasons[b])
      )
    })
}

/**
 * 국내 숙소 상품쿠폰 사용 불가 사유 조회
 */
export const getDomesticCouponUnavailableReasonText = (
  unavailableReasons: string[],
  coupon: TDomesticCoupon
) => {
  if (!unavailableReasons.length) {
    return
  }

  const sortedUnavailableReason = sortUnavailableReason(unavailableReasons)
  const unavailableReason = sortedUnavailableReason[0]
  if (unavailableReason === 'applicableminprice') {
    return domesticCouponUnavailableReasonsText[unavailableReason].replace(
      '{applicableminprice}',
      toKRW(coupon.applicableMinPrice)
    )
  } else if (unavailableReason === 'checkoutdateperiod') {
    if (!coupon.checkinPeriod?.endAt) return ''
    return domesticCouponUnavailableReasonsText[unavailableReason].replace(
      '{checkinPeriodEndAt}',
      formatISODate(coupon.checkinPeriod.endAt, 'yyyy.MM.dd')
    )
  } else {
    return domesticCouponUnavailableReasonsText[unavailableReason]
  }
}

export const getCouponSelectModalPropertyItemCoupons = ({
  coupons,
  typeIdx,
  property,
}: {
  property: Pick<MaxCouponDiscountPropertyRequiredParams, 'id' | 'checkInDate' | 'useType'>
  coupons: PropertyMashup[number]['coupons']
  typeIdx: number
}): IDomesticOrderDiscountCouponSelectModalCoupon[] => {
  return coupons.map(coupon => {
    const discountAmount = coupon.calculatedPrice
    const filterUnavailableReasons = coupon.unavailableReasons.filter(
      reason =>
        domesticCouponUnavailableReasons[reason.toLowerCase()] ||
        domesticMillionUnavailableReasons[reason.toLowerCase()]
    )
    const unavailableReasonText = getDomesticCouponUnavailableReasonText(
      filterUnavailableReasons,
      coupon
    )

    const isB2BCoupon = getIsPropertyB2BCoupon(coupon.couponType)
    /**
     * api 에서 progress 가 null 로 넘어오는경우 무제한으로 간주함.
     */
    const UNLIMITED_COUPON_COUNT = 99
    const remainCount = coupon.progress
      ? (coupon.progress?.limitUse ?? 0) - (coupon.progress?.used ?? 0)
      : UNLIMITED_COUPON_COUNT

    const couponData = {
      id: coupon.id,
      couponId: coupon.metaCouponId,
      couponName: coupon.couponName,
      amount: discountAmount,
      amountText: `${toKRW(discountAmount)} 할인`,
      available: !unavailableReasonText,
      isNolCoupon: getIsNolCoupon(coupon.couponType),
      unavailableReasonText,
      typeIdx,
      allowAdditional: coupon.allowAdditional,
      applicationType: coupon.applicationType,
    }

    if (isB2BCoupon) {
      return {
        ...couponData,
        isB2BCoupon: true,
        remainCount: remainCount >= 0 ? remainCount : 0,
        b2bCouponCacheStockKey: `${coupon.metaCouponId}-${typeIdx}`,
        productInfo: {
          isProperty: true,
          placeId: property.id,
          useType: property.useType,
          checkInDate: property.checkInDate,
        },
      }
    }

    return { ...couponData, isB2BCoupon: false }
  })
}
